import { computed, nextTick, ref, shallowRef, watch } from 'vue'
import { useDebouncedRef } from './useDebouncedRef'
import { defaultGetItemId } from './gpx-select-option-utils'
import { useGpxSelectResolvedOption } from './useGpxSelectResolvedOption'
import type { UseGpxRemoteStaticSelectOptions } from '../remote-select/types'
import { remoteOptionsWithLoadingPlaceholder } from '../remote-select/remote-options'
import { isRemoteSelectEnabled } from '../remote-select/utils'

export type {
  GpxRemoteSelectFetchOptions,
  UseGpxRemoteStaticSelectOptions,
} from '../remote-select/types'

export function useGpxRemoteStaticSelect<T>(
  options: UseGpxRemoteStaticSelectOptions<T>,
) {
  const debounceMs = options.debounceMs ?? 300
  const getItemId = options.getItemId ?? defaultGetItemId<T>

  const filterQuery = ref('')
  const { debounced: debouncedQuery } = useDebouncedRef(filterQuery, debounceMs)

  const items = shallowRef<T[]>([])
  const loading = ref(false)
  const hasLoadedOnce = ref(false)
  let loadGeneration = 0

  const { resolvedSelected, cacheSelected } = useGpxSelectResolvedOption<T>({
    modelValue: options.modelValue,
    getItemId,
    resolveSelected: options.resolveSelected,
    findInItems: (value) =>
      items.value.find((entry) => getItemId(entry) === value) ?? null,
  })

  function resetItems(resetOptions?: { loading?: boolean; clearSelected?: boolean }) {
    loadGeneration += 1
    items.value = []
    if (resetOptions?.clearSelected) {
      resolvedSelected.value = null
    }
    hasLoadedOnce.value = false
    loading.value = resetOptions?.loading ?? false
  }

  async function loadOptions() {
    if (!isRemoteSelectEnabled(options.enabled)) {
      return
    }

    const generation = loadGeneration
    const query = debouncedQuery.value.trim()
    loading.value = true

    try {
      const data = await options.fetchOptions({ query })
      if (generation !== loadGeneration) {
        return
      }
      items.value = data
      hasLoadedOnce.value = true
    } finally {
      if (generation === loadGeneration) {
        loading.value = false
      }
    }
  }

  function onFilter(event: { value: string }) {
    filterQuery.value = event.value ?? ''
  }

  function onOverlayShow() {
    if (!isRemoteSelectEnabled(options.enabled)) {
      return
    }
    if (!hasLoadedOnce.value) {
      loading.value = true
      void loadOptions()
    }
  }

  watch(debouncedQuery, async () => {
    if (!isRemoteSelectEnabled(options.enabled)) {
      return
    }
    resetItems({ loading: true })
    await nextTick()
    await loadOptions()
  })

  watch(
    () => isRemoteSelectEnabled(options.enabled),
    (enabled) => {
      if (!enabled) {
        resetItems({ clearSelected: true })
      } else {
        void loadOptions()
      }
    },
    { immediate: true },
  )

  const optionsForSelect = computed(() =>
    remoteOptionsWithLoadingPlaceholder(
      items.value,
      hasLoadedOnce.value,
      loading.value,
      resolvedSelected.value != null,
    ),
  )

  const showEmptyMessage = computed(
    () => hasLoadedOnce.value && items.value.length === 0 && !loading.value,
  )

  return {
    options: optionsForSelect,
    onFilter,
    loading,
    hasLoadedOnce,
    showEmptyMessage,
    resolvedSelected,
    filterQuery,
    debouncedQuery,
    loadOptions,
    onOverlayShow,
    totalRecords: computed(() => items.value.length),
    virtualScrollerOptions: computed(() => undefined),
    cacheSelected,
  }
}
